import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute, ProtectedRouteOnly, AdminRoute } from './components/ProtectedRoute';
import AppShell from './components/AppShell';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import ChangePassword from './pages/ChangePassword';
import Search from './pages/Search';
import Users from './pages/Users';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import SuppliersPage from './pages/SuppliersPage';
import SupplierDetailPage from './pages/SupplierDetailPage';
import InventoryPage from './pages/InventoryPage';
import ItemDetailPage from './pages/ItemDetailPage';
import EstimatesPage from './pages/EstimatesPage';
import EstimateFormPage from './pages/EstimateFormPage';
import EstimateDetailPage from './pages/EstimateDetailPage';
import CountertopJobsPage from './pages/CountertopJobsPage';
import CountertopJobFormPage from './pages/CountertopJobFormPage';
import CountertopJobDetailPage from './pages/CountertopJobDetailPage';
import PaymentsPage from './pages/PaymentsPage';
import ReturnsPage from './pages/ReturnsPage';
import ReturnDetailPage from './pages/ReturnDetailPage';
import CreateReturnPage from './pages/CreateReturnPage';
import ExpensesPage from './pages/ExpensesPage';
import StockAdjustmentsPage from './pages/StockAdjustmentsPage';
import SettingsPage from './pages/SettingsPage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import PurchaseOrderFormPage from './pages/PurchaseOrderFormPage';
import PurchaseOrderDetailPage from './pages/PurchaseOrderDetailPage';
import PoPaymentsPage from './pages/PoPaymentsPage';
import PurchaseReturnsPage from './pages/PurchaseReturnsPage';
import PurchaseReturnDetailPage from './pages/PurchaseReturnDetailPage';
import CreatePurchaseReturnPage from './pages/CreatePurchaseReturnPage';
import ReportsIndexPage from './pages/ReportsIndexPage';
import DailyCashReport from './pages/reports/DailyCashReport';
import CashStatementReport from './pages/reports/CashStatementReport';
import OutstandingEstimatesReport from './pages/reports/OutstandingEstimatesReport';
import StockReport from './pages/reports/StockReport';
import SupplierPayablesReport from './pages/reports/SupplierPayablesReport';
import ProfitMarginReport from './pages/reports/ProfitMarginReport';
import ArAgingReport from './pages/reports/ArAgingReport';
import ApAgingReport from './pages/reports/ApAgingReport';
import StockValuationReport from './pages/reports/StockValuationReport';
import PickupsReport from './pages/reports/PickupsReport';
import ItemSalesReport from './pages/reports/ItemSalesReport';
import ItemPurchasesReport from './pages/reports/ItemPurchasesReport';
import SalesByItemReport from './pages/reports/SalesByItemReport';
import PurchasesByItemReport from './pages/reports/PurchasesByItemReport';
import BulkWriteOffPage from './pages/BulkWriteOffPage';
import AuditLogPage from './pages/AuditLogPage';
import BackupRestorePage from './pages/BackupRestorePage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Auth-required, standalone page (no AppShell) */}
            <Route
              path="/change-password"
              element={
                <ProtectedRouteOnly>
                  <ChangePassword />
                </ProtectedRouteOnly>
              }
            />

            {/* Auth-required + AppShell */}
            <Route element={<ProtectedRoute />}>
              <Route element={<AppShell />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/search" element={<Search />} />

                {/* Countertop Jobs */}
                <Route path="/countertop-jobs" element={<CountertopJobsPage />} />
                <Route path="/countertop-jobs/new" element={<CountertopJobFormPage />} />
                <Route path="/countertop-jobs/:id" element={<CountertopJobDetailPage />} />
                <Route path="/countertop-jobs/:id/edit" element={<CountertopJobFormPage />} />

                {/* Phase 2 — Masters */}
                <Route path="/customers" element={<CustomersPage />} />
                <Route path="/customers/:id" element={<CustomerDetailPage />} />
                <Route path="/suppliers" element={<SuppliersPage />} />
                <Route path="/suppliers/:id" element={<SupplierDetailPage />} />
                <Route path="/items" element={<InventoryPage />} />
                <Route path="/items/:id" element={<ItemDetailPage />} />

                {/* Phase 3 — Estimates & Payments */}
                <Route path="/estimates" element={<EstimatesPage />} />
                <Route path="/estimates/new" element={<EstimateFormPage />} />
                <Route path="/estimates/:id" element={<EstimateDetailPage />} />
                <Route path="/estimates/:id/edit" element={<EstimateFormPage />} />
                <Route path="/estimates/:id/return" element={<CreateReturnPage />} />
                <Route path="/payments" element={<PaymentsPage />} />

                {/* Phase 4 — Sales Returns */}
                <Route path="/returns" element={<ReturnsPage />} />
                <Route path="/returns/:id" element={<ReturnDetailPage />} />
                <Route path="/returns/:id/edit" element={<CreateReturnPage mode="edit" />} />

                {/* Phase 7 — Purchase Orders */}
                <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
                <Route path="/purchase-orders/new" element={<PurchaseOrderFormPage />} />
                <Route path="/purchase-orders/:id" element={<PurchaseOrderDetailPage />} />
                <Route path="/purchase-orders/:id/edit" element={<PurchaseOrderFormPage />} />
                <Route path="/po-payments" element={<PoPaymentsPage />} />
                <Route path="/purchase-orders/:id/return" element={<CreatePurchaseReturnPage />} />

                {/* Phase 8 — Purchase Returns */}
                <Route path="/purchase-returns" element={<PurchaseReturnsPage />} />
                <Route path="/purchase-returns/:id" element={<PurchaseReturnDetailPage />} />
                <Route
                  path="/purchase-returns/:id/edit"
                  element={<CreatePurchaseReturnPage mode="edit" />}
                />

                {/* Admin-only */}
                <Route element={<AdminRoute />}>
                  {/* Phase 5 — admin-only */}
                  <Route path="/expenses" element={<ExpensesPage />} />
                  <Route path="/stock-adjustments" element={<StockAdjustmentsPage />} />

                  {/* Phase 9 — Reports (admin-only) */}
                  <Route path="/reports" element={<ReportsIndexPage />} />
                  <Route path="/reports/daily-cash" element={<DailyCashReport />} />
                  <Route path="/reports/cash-statement" element={<CashStatementReport />} />
                  <Route
                    path="/reports/outstanding-estimates"
                    element={<OutstandingEstimatesReport />}
                  />
                  <Route path="/reports/stock" element={<StockReport />} />
                  <Route path="/reports/supplier-payables" element={<SupplierPayablesReport />} />
                  <Route path="/reports/profit-margin" element={<ProfitMarginReport />} />
                  <Route path="/reports/ar-aging" element={<ArAgingReport />} />
                  <Route path="/reports/ap-aging" element={<ApAgingReport />} />
                  <Route path="/reports/stock-valuation" element={<StockValuationReport />} />
                  <Route path="/reports/pickups" element={<PickupsReport />} />
                  <Route path="/reports/item-sales" element={<ItemSalesReport />} />
                  <Route path="/reports/item-purchases" element={<ItemPurchasesReport />} />
                  <Route path="/reports/sales-by-item" element={<SalesByItemReport />} />
                  <Route path="/reports/purchases-by-item" element={<PurchasesByItemReport />} />
                  <Route path="/reports/bulk-write-off" element={<BulkWriteOffPage />} />

                  <Route path="/users" element={<Users />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/admin/audit-log" element={<AuditLogPage />} />
                  <Route path="/admin/backup" element={<BackupRestorePage />} />
                </Route>
              </Route>
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
